import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3007';

/**
 * PaymentCallback — post-payment redirect handler.
 * Reads checkout_session_id from URL params, polls payment status until
 * completed, then retries session creation with the paymentId.
 */
const PaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying | creating | success | error
  const [errorMessage, setErrorMessage] = useState(null);
  const pollingRef = useRef(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const checkoutSessionId = searchParams.get('checkout_session_id');

    if (!checkoutSessionId) {
      setStatus('error');
      setErrorMessage('No checkout session ID found. Payment may have been cancelled.');
      return;
    }

    if (!user) {
      setStatus('error');
      setErrorMessage('You must be signed in to complete payment.');
      return;
    }

    let attempts = 0;
    const maxAttempts = 30; // ~30 seconds at 1s intervals

    const pollPaymentStatus = async () => {
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${API_URL}/api/payment/status/${checkoutSessionId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to check payment status');
        }

        const data = await response.json();

        if (data.status === 'completed') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          await createSessionWithPayment(checkoutSessionId, token);
        } else if (data.status === 'expired') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setStatus('error');
          setErrorMessage('Payment session expired. Please try again.');
        } else {
          attempts++;
          if (attempts >= maxAttempts) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
            setStatus('error');
            setErrorMessage(
              'Payment verification timed out. If you were charged, please contact support.'
            );
          }
        }
      } catch (err) {
        console.error('Payment polling error:', err);
        attempts++;
        if (attempts >= maxAttempts) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setStatus('error');
          setErrorMessage('Failed to verify payment. Please contact support.');
        }
      }
    };

    // Start polling
    pollPaymentStatus();
    pollingRef.current = setInterval(pollPaymentStatus, 1000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createSessionWithPayment = async (paymentId, token) => {
    setStatus('creating');

    try {
      // Retrieve pending session data from sessionStorage
      const pendingRaw = sessionStorage.getItem('pendingSessionData');
      if (!pendingRaw) {
        setStatus('error');
        setErrorMessage(
          'Session data not found. Please go back and create the session again.'
        );
        return;
      }

      const sessionData = JSON.parse(pendingRaw);
      sessionData.paymentId = paymentId;

      const response = await fetch(`${API_URL}/api/sessions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(sessionData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create session');
      }

      const data = await response.json();
      sessionStorage.removeItem('pendingSessionData');
      setStatus('success');

      // Navigate to the newly created session
      setTimeout(() => {
        navigate(`/session/${data.session.id}`, { replace: true });
      }, 1500);
    } catch (err) {
      console.error('Session creation after payment error:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Failed to create session after payment.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="max-w-md w-full text-center">
        {status === 'verifying' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">
              Verifying Payment...
            </h2>
            <p className="text-gray-400">
              Please wait while we confirm your payment.
            </p>
          </>
        )}

        {status === 'creating' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">
              Creating Session...
            </h2>
            <p className="text-gray-400">
              Payment confirmed! Setting up your session now.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="h-12 w-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-green-600">
              <svg
                className="h-6 w-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              Session Created!
            </h2>
            <p className="text-gray-400">Redirecting you to your session...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="h-12 w-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-red-600">
              <svg
                className="h-6 w-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white mb-2">
              Something Went Wrong
            </h2>
            <p className="text-gray-400 mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="px-6 py-2 rounded-lg bg-purple-600 text-white font-semibold hover:bg-purple-700 transition"
            >
              Back to Home
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
